// @ts-strict-ignore
import {
	babDetection,
	communicationService,
	context,
	TEMPLATE,
	TemplateStateHandler,
	TemplateTransition,
	UapLoadStatus,
	universalAdPackage,
	utils,
	type AdSlot,
} from '@wikia/ad-engine';
import { Inject, Injectable } from '@wikia/dependency-injection';
import {
	AD_ENGINE_UAP_CUSTOM_LOADER_CALLED,
	AD_ENGINE_UAP_LOAD_STATUS,
} from '../../../../../communication/events/events-ad-engine-uap';

@Injectable({ autobind: false })
export class StickyTlbBlockingHandler implements TemplateStateHandler {
	static LOG_GROUP = 'sticky-tlb';

	constructor(@Inject(TEMPLATE.SLOT) private adSlot: AdSlot) {}

	async onEnter(transition: TemplateTransition<'initial' | 'blocking'>): Promise<void> {
		const isUap = await this.isUAP();
		if (isUap) {
			this.blockStickyTLB('UAP');
			return;
		}
		if (!this.isStickyTlbForced() && !this.enabledByLine() && !this.enabledByOrder()) {
			this.blockStickyTLB(`Line item ID ${this.adSlot.lineItemId}`);
			return;
		}
		if (babDetection.isBlocking()) {
			this.blockStickyTLB('AdBlock');
			return;
		}

		this.disableOnDelayedUap(transition);

		transition('initial');
	}

	private disableOnDelayedUap(transition: TemplateTransition<'blocking'>): void {
		communicationService.on(AD_ENGINE_UAP_CUSTOM_LOADER_CALLED, (action) => {
			if (action?.adProduct === 'vuap' || action?.adProduct === 'uap') {
				transition('blocking');
			}
		});
	}

	private blockStickyTLB(reason: string): void {
		this.adSlot.emitEvent(universalAdPackage.SLOT_STICKINESS_DISABLED);
		this.logger(`Template 'stickyTlb' could not be applied for ${reason}`);
	}

	private enabledByLine(): boolean {
		const lines: string[] = context.get('templates.stickyTlb.lineItemIds') || [];

		return this.checkRolloutVariable(this.adSlot.lineItemId, lines);
	}

	private enabledByOrder(): boolean {
		const orders: string[] = context.get('templates.stickyTlb.ordersIds') || [];

		return this.checkRolloutVariable(this.adSlot.orderId, orders);
	}

	private checkRolloutVariable(gamId: string | number, enabledIds: string[]): boolean {
		if (!gamId || !enabledIds || !Array.isArray(enabledIds)) {
			return false;
		}

		return enabledIds.some((id) => {
			return id.toString() === gamId.toString();
		});
	}

	private async isUAP(): Promise<boolean> {
		return new Promise((resolve) => {
			communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
				resolve(action.isLoaded);
			});
		});
	}

	private isStickyTlbForced(): boolean {
		return (
			context.get('templates.stickyTlb.forced') || context.get('templates.stickyTlb.experiment')
		);
	}

	private logger(...logMsgs: any): void {
		utils.logger(StickyTlbBlockingHandler.LOG_GROUP, ...logMsgs);
	}
}
